class AAHeadquartersDashboard {
  constructor(root) {
    this.root = root;
    this.loading = true;
    this.error = '';
    this.failedSections = new Set();
    this.clients = [];
    this.tasks = [];
    this.attention = {};
    this.pendingTeamRequests = [];
    this.pendingTeamRequestsUnavailable = false;
    this.teamMembers = [];
    this.teamSummary = [];
    this.packageRows = [];
    this.playbooks = [];
    this.catalogs = [];
    this.batches = [];
    this.currentUser = null;
    this.currentTeamMember = null;
    this.month = this.getCurrentMonthValue();
    this.leoMonthLoading = false;
    this.leoMonthError = '';
    this.leoMonthRequestId = 0;
    this.leoUploadOpen = false;
    this.leoUploadBusy = false;
    this.leoUploadMessage = '';
    this.leoExpanded = false;
    this.leoCatalogError = '';

    this.bindEvents();
    this.load();
  }

  escapeHtml(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  getCurrentMonthValue() {
    const date = new Date();
    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0')
    ].join('-');
  }

  getMonthParts() {
    const [year, month] = this.month.split('-').map(Number);
    return { year, month };
  }

  getTodayValue() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  getGreeting() {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  }

  formatMonth(value = this.month) {
    const [year, month] = String(value || '').split('-').map(Number);
    if (!year || !month) return value || '';

    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      year: 'numeric'
    }).format(new Date(year, month - 1, 1));
  }

  formatDate(value = '') {
    if (!value) return 'No due date';

    const parts = String(value).slice(0, 10).split('-').map(Number);
    if (parts.length !== 3 || parts.some((item) => !item)) {
      return value;
    }

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric'
    }).format(new Date(parts[0], parts[1] - 1, parts[2]));
  }

  getClientName(clientId) {
    const client = this.clients.find(
      (item) => String(item.id) === String(clientId || '')
    );

    return client?.business_name || 'Internal';
  }

  normalizeStatus(value = '') {
    return String(value || '').trim().toLowerCase();
  }

  isTaskClosed(task) {
    return ['completed', 'archived'].includes(
      this.normalizeStatus(task?.status)
    );
  }

  getMyTasks() {
    const teamMemberId = this.currentTeamMember?.id;

    if (!teamMemberId) {
      return [];
    }

    return this.tasks.filter(
      (task) =>
        String(task.assignee_team_member_id || '') ===
          String(teamMemberId) &&
        !this.isTaskClosed(task)
    );
  }

  getTaskMetrics() {
    const today = this.getTodayValue();
    const myTasks = this.getMyTasks();

    const dueToday = myTasks.filter(
      (task) => String(task.due_date || '') === today
    ).length;

    const overdue = myTasks.filter(
      (task) =>
        task.due_date &&
        String(task.due_date) < today
    ).length;

    const waiting = myTasks.filter((task) =>
      ['waiting', 'issue'].includes(
        this.normalizeStatus(task.status)
      )
    ).length;

    return {
      open: myTasks.length,
      dueToday,
      overdue,
      waiting
    };
  }

  getUpcomingTasks(limit = 3) {
    const today = this.getTodayValue();

    return this.getMyTasks()
      .slice()
      .sort((a, b) => {
        const aDate = a.due_date || '9999-12-31';
        const bDate = b.due_date || '9999-12-31';

        if (aDate === bDate) {
          return String(a.specific_item || '').localeCompare(
            String(b.specific_item || '')
          );
        }

        return aDate.localeCompare(bDate);
      })
      .slice(0, limit)
      .map((task) => ({
        ...task,
        isOverdue:
          Boolean(task.due_date) &&
          String(task.due_date) < today
      }));
  }

  getAttentionRows() {
    const metrics = this.getTaskMetrics();
    const attention = this.attention || {};
    const tasksUnavailable = ['tasks', 'team members', 'current user']
      .some((name) => this.failedSections.has(name));
    const attentionUnavailable = this.failedSections.has('attention items');

    return [
      {
        label: 'My overdue tasks',
        value: tasksUnavailable ? '—' : metrics.overdue,
        href: '/pages/artisans-avenue-my-work'
      },
      {
        label: 'Package change requests',
        value: attentionUnavailable ? '—' : Number(attention.packageRequestCount || 0),
        href: '/pages/artisans-avenue-operations'
      },
      {
        label: 'Access issues',
        value: attentionUnavailable ? '—' : Number(attention.accessIssueCount || 0),
        href: '/pages/artisans-avenue-operations'
      },
      {
        label: 'Contract alerts',
        value:
          attentionUnavailable ? '—' : Number(attention.contractAttentionCount || 0),
        href: '/pages/artisans-avenue-operations'
      },
      {
        label: 'Blocked tasks',
        value: attentionUnavailable ? '—' : Number(attention.blockedTaskCount || 0),
        href: '/pages/artisans-avenue-operations'
      }
    ];
  }

  isPastLeoMonth(value) {
    return /^\d{4}-\d{2}$/.test(String(value || '')) &&
      String(value) < this.getCurrentMonthValue();
  }

  async refreshLeoCatalogs() {
    const requestId = ++this.leoMonthRequestId;
    this.leoMonthLoading = true;
    this.leoMonthError = '';
    this.render();
    const db = window.AASupabaseClient?.getClient?.();
    if (!db) {
      this.leoMonthLoading = false;
      this.leoMonthError = 'Catalog connection is unavailable.';
      this.render();
      return;
    }
    try {
      // Leo is the active planning queue, including work finished ahead.
      // A November catalog appears alongside October without selecting a month.
      const fromMonth = this.getCurrentMonthValue() + '-01';
      const [catalogsResult, batchesResult] = await Promise.all([
        db.from('aa_catalog_records')
          .select('id, client_id, catalog_month, catalog_title, status, catalog_pdf_storage_path, catalog_url')
          .eq('status', 'active').gte('catalog_month', fromMonth)
          .order('catalog_month', {ascending:true}),
        db.from('aa_task_generation_batches')
          .select('id, client_id, catalog_id, catalog_month, status, created_at, updated_at')
          .gte('catalog_month', fromMonth)
          .neq('status', 'superseded')
          .order('created_at', {ascending:false})
      ]);
      if(catalogsResult.error) throw catalogsResult.error;
      if(batchesResult.error) throw batchesResult.error;
      if(requestId !== this.leoMonthRequestId) return;
      this.catalogs = catalogsResult.data || [];
      this.batches = batchesResult.data || [];
    } catch(error) {
      if(requestId !== this.leoMonthRequestId) return;
      this.leoMonthError = 'Unable to load catalog planning. Try again.';
      console.warn('Leo catalog planning refresh failed:',error);
      // Keep any previously loaded results visible on a transient refresh error.
    } finally {
      if(requestId === this.leoMonthRequestId){
        this.leoMonthLoading=false;
        this.render();
      }
    }
  }

  getLeoRows() {
    const batchesByCatalog = new Map();

    this.batches.forEach((batch) => {
      const key = String(batch.catalog_id || '');
      if (key && !batchesByCatalog.has(key)) {
        batchesByCatalog.set(key, batch);
      }
    });

    const priority = {
      failed: 0,
      draft: 1,
      generating: 2,
      none: 3,
      approved: 4,
      undone: 5
    };

    return this.catalogs
      .map((catalog) => {
        const batch =
          batchesByCatalog.get(String(catalog.id)) || null;

        const status = batch?.status || 'none';

        const labels = {
          generating: 'Leo is working',
          draft: 'Ready for review',
          approved: 'Approved',
          undone: 'Undone',
          failed: 'Needs attention',
          none: 'Not generated'
        };

        return {
          catalog,
          batch,
          status,
          label: labels[status] || status
        };
      })
      .sort((a, b) => {
        const byMonth = String(a.catalog.catalog_month).localeCompare(String(b.catalog.catalog_month));
        if (byMonth !== 0) return byMonth;
        const aPriority = priority[a.status] ?? 99;
        const bPriority = priority[b.status] ?? 99;

        if (aPriority !== bPriority) {
          return aPriority - bPriority;
        }

        return this.getClientName(a.catalog.client_id)
          .localeCompare(this.getClientName(b.catalog.client_id));
      });
  }

  getLeoActionRows(limit = 4) {
    return this.getLeoRows()
      .filter((row) =>
        ['draft', 'failed', 'generating'].includes(row.status)
      )
      .slice(0, limit);
  }

  getLeoMetrics() {
    return this.getLeoRows().reduce(
      (totals, row) => {
        totals.total += 1;

        if (row.status === 'draft') {
          totals.review += 1;
        } else if (row.status === 'approved') {
          totals.approved += 1;
        } else if (row.status === 'generating') {
          totals.working += 1;
        } else if (row.status === 'failed') {
          totals.attention += 1;
        } else if (row.status === 'none') {
          totals.notGenerated += 1;
        }

        return totals;
      },
      {
        total: 0,
        review: 0,
        approved: 0,
        working: 0,
        attention: 0,
        notGenerated: 0
      }
    );
  }

  getPlaybookRows(limit = 3) {
    return this.playbooks
      .slice()
      .sort((a, b) =>
        String(b.updated_at || '').localeCompare(
          String(a.updated_at || '')
        )
      )
      .slice(0, limit);
  }

  isMonthlyPackageRow(row) {
    const unit = String(row?.unitLabel || '').toLowerCase();

    return !(
      unit.includes('year') ||
      unit.includes('quarter') ||
      unit === 'included'
    );
  }

  getClientProductionRows() {
    const grouped = new Map();

    this.packageRows
      .filter((row) => this.isMonthlyPackageRow(row))
      .forEach((row) => {
        const key = String(row.clientId || '');

        if (!key) return;

        if (!grouped.has(key)) {
          grouped.set(key, {
            clientId: row.clientId,
            clientCode: row.clientCode || '',
            clientName:
              row.clientName ||
              this.getClientName(row.clientId),
            contracted: 0,
            completed: 0
          });
        }

        const item = grouped.get(key);
        item.contracted += Number(row.contracted || 0);
        item.completed += Number(row.completed || 0);
      });

    return [...grouped.values()]
      .map((item) => ({
        ...item,
        percent:
          item.contracted > 0
            ? Math.max(
                0,
                Math.min(
                  100,
                  Math.round(
                    (item.completed / item.contracted) * 100
                  )
                )
              )
            : 0
      }))
      .sort((a, b) =>
        String(a.clientCode || a.clientName)
          .localeCompare(
            String(b.clientCode || b.clientName)
          )
      );
  }

  getProductionTotals() {
    return this.getClientProductionRows()
      .reduce(
        (totals, item) => {
          totals.contracted += item.contracted;
          totals.completed += item.completed;
          return totals;
        },
        {
          contracted: 0,
          completed: 0
        }
      );
  }

  renderProductionRow(item) {
    const label =
      item.clientCode ||
      item.clientName ||
      'Client';

    return `
      <div class="aa-dashboard-production-row">
        <span
          class="aa-dashboard-production-row__label"
          title="${this.escapeHtml(item.clientName || label)}"
        >
          ${this.escapeHtml(label)}
        </span>
        <div class="aa-dashboard-production-row__track">
          <span
            class="aa-dashboard-production-row__fill"
            style="width: ${this.escapeHtml(item.percent)}%"
          ></span>
        </div>
        <strong>${this.escapeHtml(item.percent)}%</strong>
      </div>
    `;
  }

  renderTeamSummary(member) {
    const completion = Number(member.completionPercent || 0);

    return `
      <article class="aa-dashboard-assignee">
        <div class="aa-dashboard-assignee__header">
          <strong>
            ${this.escapeHtml(member.teamMemberName || 'Team Member')}
          </strong>
          <span>${this.escapeHtml(completion)}% complete</span>
        </div>

        <div class="aa-dashboard-assignee__grid">
          <span>Completed <strong>${this.escapeHtml(member.completed || 0)}</strong></span>
          <span>Not Started <strong>${this.escapeHtml(member.notStarted || 0)}</strong></span>
          <span>In Progress <strong>${this.escapeHtml(member.inProgress || 0)}</strong></span>
          <span>Waiting <strong>${this.escapeHtml(member.waiting || 0)}</strong></span>
          <span>Issue <strong>${this.escapeHtml(member.issue || 0)}</strong></span>
          <span>Total <strong>${this.escapeHtml(member.total || 0)}</strong></span>
        </div>
      </article>
    `;
  }

  async safe(label, action, fallback) {
    try {
      const result = await action();
      if (result?.error) throw result.error;
      return result;
    } catch (error) {
      this.failedSections.add(label);
      console.warn(`Dashboard: unable to load ${label}`, error);
      return fallback;
    }
  }

  async load() {
    this.loading = true;
    this.error = '';
    this.failedSections.clear();
    this.pendingTeamRequests = [];
    this.pendingTeamRequestsUnavailable = false;
    this.render();

    const api = window.AASupabaseClient;
    const db = api?.getClient?.();

    if (!api || !db) {
      this.loading = false;
      this.error = 'Headquarters data is unavailable.';
      this.render();
      return;
    }

    const { year, month } = this.getMonthParts();
    const leoFromMonth = `${this.getCurrentMonthValue()}-01`;

    try {
      const [
        clients,
        tasks,
        attention,
        teamMembers,
        teamSummary,
        authResult,
        playbooksResult,
        packageRowsResult,
        catalogsResult,
        batchesResult
      ] = await Promise.all([
        this.safe(
          'clients',
          () => api.getClients(),
          []
        ),
        this.safe(
          'tasks',
          () => api.getTasks(),
          []
        ),
        this.safe(
          'attention items',
          () => api.getOperationsAttention(),
          {}
        ),
        this.safe(
          'team members',
          () => api.getInternalTeamMembers(),
          []
        ),
        this.safe(
          'team summary',
          () => api.getTeamTaskSummary({ month: this.month }),
          []
        ),
        this.safe(
          'current user',
          () => db.auth.getUser(),
          { data: { user: null } }
        ),
        this.safe(
          'playbooks',
          async () => {
            const result = await db
              .from('aa_playbooks')
              .select('id, client_id, month, year, status, progress, updated_at')
              .eq('year', year)
              .eq('month', month);

            if (result.error) throw result.error;
            return result.data || [];
          },
          []
        ),
        this.safe(
          'production summary',
          () => api.getOperationsPackageSummary({ month: this.month }),
          []
        ),
        this.safe(
          'current catalogs',
          async () => {
            const result = await db
              .from('aa_catalog_records')
              .select('id, client_id, catalog_month, catalog_title, status, catalog_pdf_storage_path, catalog_url')
              .eq('status', 'active')
              .gte('catalog_month', leoFromMonth)
              .order('catalog_month', {ascending:true});

            if (result.error) throw result.error;
            return result.data || [];
          },
          []
        ),
        this.safe(
          'Leo batches',
          async () => {
            const result = await db
              .from('aa_task_generation_batches')
              .select('id, client_id, catalog_id, catalog_month, status, created_at, updated_at')
              .gte('catalog_month', leoFromMonth)
              .neq('status', 'superseded')
              .order('created_at', { ascending: false });

            if (result.error) throw result.error;
            return result.data || [];
          },
          []
        )
      ]);

      this.clients = Array.isArray(clients) ? clients : [];
      this.tasks = Array.isArray(tasks) ? tasks : [];
      this.attention =
        attention && typeof attention === 'object'
          ? attention
          : {};
      this.teamMembers =
        Array.isArray(teamMembers) ? teamMembers : [];
      this.teamSummary =
        Array.isArray(teamSummary) ? teamSummary : [];
      this.currentUser = authResult?.data?.user || null;
      if (!this.currentUser) this.failedSections.add('current user');
      this.playbooks =
        Array.isArray(playbooksResult) ? playbooksResult : [];
      this.packageRows =
        Array.isArray(packageRowsResult) ? packageRowsResult : [];
      this.catalogs =
        Array.isArray(catalogsResult) ? catalogsResult : [];
      this.batches =
        Array.isArray(batchesResult) ? batchesResult : [];

      this.currentTeamMember =
        this.teamMembers.find(
          (member) =>
            String(member.user_id || '') ===
            String(this.currentUser?.id || '')
        ) || null;

      // Client employee approval inbox is read from an administrator-only service.
      // Failure here must not interrupt production statistics or the rest of Dashboard.
      try {
        const config = window.AA_SUPABASE_CONFIG;
        const { data: authSession } = await db.auth.getSession();
        const accessToken = authSession?.session?.access_token;
        if (accessToken && config?.url && config?.anonKey) {
          const reply = await fetch(config.url + '/functions/v1/aa-client-team-service', {
            method: 'POST',
            headers: {
              Authorization: 'Bearer ' + accessToken,
              apikey: config.anonKey,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ action: 'pending-requests' })
          });
          if (reply.status === 403) {
            // HQ staff without admin privileges cannot see the company-wide queue.
            this.pendingTeamRequestsUnavailable = false;
          } else {
            const payload = await reply.json().catch(() => ({}));
            if (!reply.ok || !payload.success) throw new Error('Employee requests unavailable.');
            this.pendingTeamRequests = Array.isArray(payload.data?.requests)
              ? payload.data.requests : [];
          }
        } else {
          this.pendingTeamRequestsUnavailable = true;
        }
      } catch (error) {
        this.pendingTeamRequestsUnavailable = true;
        console.warn('Team access approval inbox unavailable:', error);
      }
    } catch (error) {
      console.error('Unable to load Headquarters Dashboard:', error);
      this.error =
        error?.message ||
        'Unable to load the Headquarters Dashboard.';
    } finally {
      this.loading = false;
      this.render();
    }
  }

  renderStat(label, value, detail = '') {
    return `
      <article class="aa-dashboard-stat">
        <span>${this.escapeHtml(label)}</span>
        <strong>${this.escapeHtml(value)}</strong>
        ${detail ? `
          <small>${this.escapeHtml(detail)}</small>
        ` : ''}
      </article>
    `;
  }

  renderTaskRow(task) {
    const clientName = this.getClientName(task.client_id);

    return `
      <div class="aa-dashboard-list-row ${task.isOverdue ? 'is-alert' : ''}">
        <div class="aa-dashboard-list-row__main">
          <strong>${this.escapeHtml(task.specific_item || task.task_type || 'Task')}</strong>
          <span>
            ${this.escapeHtml(clientName)}
            ${task.task_type ? ` · ${this.escapeHtml(task.task_type)}` : ''}
          </span>
        </div>
        <div class="aa-dashboard-list-row__meta">
          <span class="aa-dashboard-date ${task.isOverdue ? 'is-alert' : ''}">
            ${this.escapeHtml(this.formatDate(task.due_date))}
          </span>
          <span class="aa-dashboard-status">
            ${this.escapeHtml(task.status || 'Not Started')}
          </span>
        </div>
      </div>
    `;
  }

  renderAttentionRow(item) {
    const active = Number(item.value || 0) > 0;

    return `
      <a
        class="aa-dashboard-attention-row ${active ? 'has-items' : ''}"
        href="${this.escapeHtml(item.href)}"
      >
        <span>${this.escapeHtml(item.label)}</span>
        <strong>${this.escapeHtml(item.value)}</strong>
      </a>
    `;
  }

  renderLeoRow(row) {
    const catalog = row.catalog;
    const hasPdf = Boolean(catalog.catalog_pdf_storage_path);
    const canGenerate = hasPdf && !this.isPastLeoMonth(String(catalog.catalog_month || '').slice(0, 7));
    const actionLabels = {
      none: 'Generate Tasks',
      draft: 'Review Tasks',
      generating: 'View Progress',
      approved: 'View Tasks',
      failed: 'Review Issue',
      undone: 'View Tasks'
    };
    const actionLabel = actionLabels[row.status] || 'View Tasks';

    return `
      <div class="aa-dashboard-list-row aa-dashboard-list-row--leo aa-dashboard-leo-action-row">
        <div class="aa-dashboard-leo-line">
          <strong>
            ${this.escapeHtml(this.getClientName(catalog.client_id))}
          </strong>
          <span>
            ${this.escapeHtml(catalog.catalog_title || this.formatMonth())}
          </span>
          <small class="aa-dashboard-leo-row-month">
            ${this.escapeHtml(this.formatMonth(String(catalog.catalog_month || '').slice(0, 7)))}
          </small>
        </div>
        <div class="aa-dashboard-leo-row-end">
          <span class="aa-dashboard-status is-${this.escapeHtml(row.status)}">
            ${this.escapeHtml(row.label)}
          </span>
          <div class="aa-dashboard-leo-row-actions">
            ${canGenerate ? `
              <button type="button"
                class="aa-dashboard-leo-action aa-dashboard-leo-action--primary"
                data-aa-generate-catalog-tasks="${this.escapeHtml(catalog.id)}"
                data-aa-generator-client-id="${this.escapeHtml(catalog.client_id)}"
                data-aa-generator-client-name="${this.escapeHtml(this.getClientName(catalog.client_id))}"
                data-aa-generator-catalog-month="${this.escapeHtml(String(catalog.catalog_month || '').slice(0, 7))}"
                data-aa-generator-catalog-title="${this.escapeHtml(catalog.catalog_title || 'Catalog')}">
                ${this.escapeHtml(actionLabel)}
              </button>
            ` : `<span class="aa-dashboard-leo-unavailable">${this.isPastLeoMonth(String(catalog.catalog_month || '').slice(0,7)) ? 'Past Catalog' : 'PDF Needed'}</span>`}
          </div>
        </div>
      </div>
    `;
  }

  renderPlaybookRow(playbook) {
    const progress = Number(playbook.progress || 0);

    return `
      <div class="aa-dashboard-list-row">
        <div class="aa-dashboard-list-row__main">
          <strong>
            ${this.escapeHtml(this.getClientName(playbook.client_id))}
          </strong>
          <span>${this.escapeHtml(playbook.status || 'In progress')}</span>
        </div>
        <span class="aa-dashboard-playbook-progress">
          ${this.escapeHtml(progress)}%
        </span>
      </div>
    `;
  }

  render() {
    if (this.loading) {
      this.root.innerHTML = `
        <section class="aa-dashboard-loading">
          <p class="aa-headquarters-loading">Loading Headquarters...</p>
        </section>
      `;
      return;
    }

    if (this.error) {
      this.root.innerHTML = `
        <section class="aa-dashboard-loading is-error">
          <p class="aa-workspace__eyebrow">Live Dashboard</p>
          <h2>Dashboard needs attention</h2>
          <p>${this.escapeHtml(this.error)}</p>
          <button
            type="button"
            class="aa-button aa-button--primary"
            data-aa-dashboard-refresh
          >
            Try Again
          </button>
        </section>
      `;
      return;
    }

    const metrics = this.getTaskMetrics();
    const leoMetrics = this.getLeoMetrics();
    const attentionRows = this.getAttentionRows();
    const leoRows = this.getLeoRows();
    const productionRows = this.getClientProductionRows();
    const productionTotals = this.getProductionTotals();
    const leoUnavailable = ['current catalogs', 'Leo batches']
      .some((name) => this.failedSections.has(name));
    const showLeoCount = (value) => leoUnavailable ? '—' : value;

    const displayName =
      this.currentTeamMember?.display_name ||
      'Headquarters';

    this.root.innerHTML = `
      <div class="aa-dashboard">
        <section class="aa-dashboard-hero">
          <div>
            <p class="aa-workspace__eyebrow">Live Dashboard</p>
            <h2>
              ${this.escapeHtml(this.getGreeting())},
              ${this.escapeHtml(displayName)}
            </h2>
            <p>${this.escapeHtml(this.formatMonth())} at a glance.</p>
          </div>

          <button
            type="button"
            class="aa-button"
            data-aa-dashboard-refresh
          >
            Refresh
          </button>
        </section>

        ${this.failedSections.size ? `
          <p class="aa-dashboard-partial-notice" role="status">
            Some Dashboard information couldn't load. Missing totals are shown as —. Use Refresh to retry.
          </p>
        ` : ''}

        <section class="aa-dashboard-command-grid aa-dashboard-command-grid--focused">
          <div class="aa-dashboard-column aa-dashboard-column--main">
            <p class="aa-dashboard-column-label">Information</p>

            <article class="aa-dashboard-panel">
              <div class="aa-dashboard-panel__header">
                <div>
                  <p class="aa-card__label">Monthly Production</p>
                  <h3>Client tasks completed</h3>
                </div>
                <span class="aa-dashboard-panel__summary">
                  By client
                </span>
              </div>

              <div class="aa-dashboard-production-grid">
                ${this.failedSections.has('production summary')
                  ? '<div class="aa-dashboard-empty">Monthly production data could not be loaded.</div>'
                  : productionRows.length
                    ? productionRows.map((item) => this.renderProductionRow(item)).join('')
                    : '<div class="aa-dashboard-empty">No contracted monthly production data yet.</div>'
                }
              </div>
            </article>

            <article class="aa-dashboard-panel aa-dashboard-panel--leo">
              <div class="aa-dashboard-panel__header">
                <div>
                  <p class="aa-card__label">Leo</p>
                  <h3>Catalog planning</h3>
                </div>

                <div class="aa-dashboard-leo-header-actions">
                  <button type="button" class="aa-dashboard-leo-header-control aa-dashboard-leo-add"
                    data-aa-dashboard-leo-add aria-expanded="${this.leoUploadOpen ? 'true' : 'false'}">
                    <span>Add Catalog</span>
                    <span class="aa-dashboard-leo-header-control__icon" aria-hidden="true">${this.leoUploadOpen ? '−' : '+'}</span>
                  </button>
                  <button type="button" class="aa-dashboard-leo-header-control"
                    data-aa-dashboard-leo-toggle
                    aria-expanded="${this.leoExpanded ? 'true' : 'false'}">
                    <span>${this.escapeHtml(showLeoCount(leoMetrics.review))} ready for review</span>
                    <span class="aa-dashboard-leo-header-control__icon" aria-hidden="true">${this.leoExpanded ? '−' : '+'}</span>
                  </button>
                </div>
              </div>

              <div class="aa-dashboard-leo-summary">
                <span><strong>${this.escapeHtml(showLeoCount(leoMetrics.review))}</strong> review</span>
                <span><strong>${this.escapeHtml(showLeoCount(leoMetrics.notGenerated))}</strong> not generated</span>
                <span><strong>${this.escapeHtml(showLeoCount(leoMetrics.working))}</strong> working</span>
                <span><strong>${this.escapeHtml(showLeoCount(leoMetrics.approved))}</strong> approved</span>
                <span><strong>${this.escapeHtml(showLeoCount(leoMetrics.attention))}</strong> attention</span>
              </div>

              ${this.leoUploadOpen ? this.renderLeoUploadForm() : ''}
              ${this.leoUploadMessage ? `<p class="aa-dashboard-leo-upload-notice" role="status">${this.escapeHtml(this.leoUploadMessage)}</p>` : ''}
              ${this.leoMonthError ? `<p class="aa-dashboard-leo-error" role="alert">${this.escapeHtml(this.leoMonthError)}</p>` : ''}

              ${this.leoExpanded ? `
                <div class="aa-dashboard-list">
                  ${this.leoMonthLoading
                    ? '<p class="aa-dashboard-empty">Loading catalog planning...</p>'
                    : leoRows.length
                      ? leoRows.map((row) => this.renderLeoRow(row)).join('')
                      : `<div class="aa-dashboard-empty">
                        ${this.leoMonthError || (leoUnavailable ? 'Leo status could not be loaded.' : 'No current or upcoming catalogs. Add one to start planning.')}
                      </div>`
                  }
                </div>
              ` : ''}
            </article>
          </div>

          <aside class="aa-dashboard-column aa-dashboard-column--side">
            <p class="aa-dashboard-column-label">Action Center</p>

            <article class="aa-dashboard-panel aa-dashboard-panel--compact aa-dashboard-panel--team-requests">
              <div class="aa-dashboard-panel__header">
                <div>
                  <p class="aa-card__label">Client Access</p>
                  <h3>Employee approvals</h3>
                </div>
                <span class="aa-dashboard-panel__summary">
                  ${this.pendingTeamRequestsUnavailable ? 'Unavailable' : this.pendingTeamRequests.length + ' pending'}
                </span>
              </div>
              <div class="aa-dashboard-message-preview aa-dashboard-message-preview--compact aa-dashboard-team-inbox">
                ${this.pendingTeamRequestsUnavailable
                  ? '<p class="aa-dashboard-team-empty">Employee requests could not load. Use Refresh to try again.</p>'
                  : this.pendingTeamRequests.length
                    ? this.pendingTeamRequests.slice(0, 6).map(item => `
                      <a class="aa-dashboard-team-row"
                         href="/pages/artisans-avenue-clients?aa_client=${encodeURIComponent(item.client_id)}&aa_tab=account&aa_section=team&aa_review=1">
                        <span><strong>${this.escapeHtml(item.client_name || 'Client')}</strong>
                          <small>${this.escapeHtml(item.requested_name || item.requested_email)} · ${this.escapeHtml(item.requested_role_key === 'client_owner' ? 'Owner' : item.requested_role_key === 'client_manager' ? 'Manager' : 'Contributor')}</small></span>
                        <span class="aa-dashboard-team-open">Review</span>
                      </a>`).join('') +
                      (this.pendingTeamRequests.length > 6
                        ? '<p class="aa-dashboard-team-empty">More requests are waiting. Review these first.</p>' : '')
                    : '<p class="aa-dashboard-team-empty">No employee requests awaiting approval.</p>'}
              </div>
            </article>

            <article class="aa-dashboard-panel aa-dashboard-panel--compact">
              <div class="aa-dashboard-panel__header">
                <div>
                  <p class="aa-card__label">Messages</p>
                  <h3>Client conversations</h3>
                </div>
                <span class="aa-dashboard-panel__summary">Next build</span>
              </div>

              <div class="aa-dashboard-message-preview aa-dashboard-message-preview--compact">
                <p>New client messages will surface here immediately.</p>
              </div>
            </article>

            <article class="aa-dashboard-panel">
              <div class="aa-dashboard-panel__header">
                <div>
                  <p class="aa-card__label">Headquarters</p>
                  <h3>Needs attention</h3>
                </div>
                <a href="/pages/artisans-avenue-operations">Open Operations</a>
              </div>

              <div class="aa-dashboard-attention-list">
                ${attentionRows.map((item) => this.renderAttentionRow(item)).join('')}
              </div>
            </article>
          </aside>
        </section>
      </div>
    `;
  }

  bindEvents() {
    this.root.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-aa-dashboard-leo-upload-form]');
      if (!form) return;
      event.preventDefault();
      void this.saveLeoCatalog(form);
    });
    // The reusable Leo task generator lives alongside the Dashboard markup.
    // Refresh row statuses when its panel closes, without reimplementing generation.
    const shell = this.root.closest('[data-aa-headquarters]');
    shell?.addEventListener('click', (event) => {
      if (event.target.closest('[data-aa-close-task-generator]')) {
        window.setTimeout(() => { this.load(); }, 150);
      }
    });

    this.root.addEventListener('click', async (event) => {
      const add = event.target.closest('[data-aa-dashboard-leo-add]');
      if (add) {
        this.leoUploadOpen = !this.leoUploadOpen;
        this.leoUploadMessage = '';
        // Adding a catalog must not automatically expand the separate review queue.
        this.render();
        return;
      }
      const cancel = event.target.closest('[data-aa-dashboard-leo-upload-cancel]');
      if (cancel && !this.leoUploadBusy) {
        this.leoUploadOpen = false;
        this.leoUploadMessage = '';
        this.render();
        return;
      }

      const refresh =
        event.target.closest('[data-aa-dashboard-refresh]');

      if (refresh) {
        this.load();
        return;
      }

      const leoToggle =
        event.target.closest('[data-aa-dashboard-leo-toggle]');

      if (leoToggle) {
        this.leoExpanded = !this.leoExpanded;
        this.render();
      }
    });
  }
}

window.AAHeadquartersDashboard = AAHeadquartersDashboard;